import * as React from "react";
import { useRouter } from "@tanstack/react-router";
import { Text } from "../../../../ui/components/Text";
import { Button } from "../../../../ui/components/Button";
import { reportBrowserError } from "../../../lib/api/browser-errors";
import { reportAnalyticsError } from "../../../lib/monitoring/product-analytics";

interface ErrorPageProps {
  error: Error;
  reset: () => void;
}

export function ErrorPage({ error, reset }: ErrorPageProps) {
  const router = useRouter();
  const [showDetails, setShowDetails] = React.useState(false);

  const isDev = import.meta.env.DEV;

  React.useEffect(() => {
    // Dual-write during the Product Analytics transition: reportAnalyticsError
    // fires unconditionally (must work pre-login too), reportBrowserError below
    // stays exactly as it was (user-service, token-gated) — not being replaced,
    // just supplemented, until analytics-service is proven out.
    reportAnalyticsError({
      kind: "js_error",
      message: error.message || "Unknown error",
      stack: error.stack,
    });

    const token =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("access_token")
        : null;
    if (!token) return;

    void reportBrowserError(token, {
      name: error.name,
      message: error.message || "Unknown error",
      stack: error.stack,
      route: router.state.location.pathname,
      url: typeof window !== "undefined" ? window.location.href : undefined,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    }).catch(() => {
      // Never let telemetry reporting interfere with the error fallback.
    });
  }, [error, router.state.location.pathname]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        padding: "40px 24px",
        background: "var(--color-bg)",
        gap: 0,
      }}
    >
      {/* Icon */}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "var(--color-dangerBg, #FEF2F2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 20,
          flexShrink: 0,
        }}
      >
        <svg
          width={26}
          height={26}
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--color-danger, #DC2626)"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>

      {/* Heading */}
      <Text level="h2" weight="bold" style={{ marginBottom: 8, textAlign: "center" }}>
        Something went wrong
      </Text>

      {/* Message */}
      <Text
        level="body-medium"
        color="muted"
        style={{ marginBottom: 28, textAlign: "center", maxWidth: 420 }}
      >
        {error.message && error.message !== "Unknown error"
          ? error.message
          : "An unexpected error occurred. You can try going back or refreshing the page."}
      </Text>

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
        <Button
          type="button"
          color="neutral"
          onClick={() => {
            router.history.back();
          }}
        >
          Go back
        </Button>
        <Button
          type="button"
          color="primary"
          onClick={() => {
            reset();
            router.invalidate();
          }}
        >
          Try again
        </Button>
      </div>

      {/* Error details (dev or toggle) */}
      {(isDev || error.stack) && (
        <div style={{ marginTop: 32, width: "100%", maxWidth: 600 }}>
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--color-fgMuted)",
              fontSize: 12,
              display: "flex",
              alignItems: "center",
              gap: 4,
              margin: "0 auto",
              padding: "4px 8px",
            }}
          >
            <svg
              width={12}
              height={12}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: showDetails ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.15s",
              }}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            {showDetails ? "Hide" : "Show"} error details
          </button>

          {showDetails && (
            <pre
              style={{
                marginTop: 12,
                padding: "14px 16px",
                background: "var(--color-muted)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                fontSize: 11,
                lineHeight: 1.6,
                overflowX: "auto",
                color: "var(--color-fg)",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {error.stack ?? error.message}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
